import React from "react";
import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation, Keyboard } from 'swiper/modules';
import { Parallax, useParallax } from 'react-scroll-parallax';
import { Link } from "react-router-dom";

const About = () => {

  const [active, setActive] = useState("marketing");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqs = [
    { question: "Are your perfumes original?", answer: "Yes, all our perfumes are 100% authentic and carefully crafted using the finest global ingredients." },
    { question: "How long does shipping take?", answer: "Standard shipping takes 3-5 business days. Express shipping options are available at checkout." },
    { question: "What is your return policy?", answer: "We offer a 14-day return window for unopened products in their original packaging." },
    { question: "Do you offer sample sizes?", answer: "Yes, we have discovery sets available so you can try our signature scents before buying a full bottle." }
  ];

  const testimonials = [
    { name: "Sarah L.", text: "AR Perfume completely changed my standard for fragrances. The lasting power is absolutely incredible!", rating: 5 },
    { name: "Michael T.", text: "The perfect blend of luxury and affordability. Every bottle feels like an experience.", rating: 5 },
    { name: "Emma W.", text: "Beautiful packaging and even better scents. I receive compliments everywhere I go.", rating: 5 }
  ];

  return (
    <>
      <div className="about-page bg-light">
        {/* 1. Premium Hero Section */}
        <section className="about-hero position-relative d-flex align-items-center" style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url("https://images.unsplash.com/photo-1615634260167-c8cdede054de?auto=format&fit=crop&q=80&w=2000")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          minHeight: '70vh',
          color: 'white'
        }}>
          <div className="container text-center py-5 mt-5">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <h1 className="display-3 fw-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Crafting Timeless Fragrances,<br />Inspiring Lasting Impressions
              </h1>
              <p className="lead fw-light mx-auto" style={{ maxWidth: '800px', letterSpacing: '1px' }}>
                Welcome to AR Perfume. A symphony of extraordinary scents designed for elegance, confidence, and those memorable moments that define your presence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. Our Story */}
        <section className="our-story py-5 my-5">
          <div className="container">
            <div className="row align-items-center g-5">
              <div className="col-lg-6">
                <motion.img
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  src={require('../assets/images/for-him/image-1.png')}
                  alt="Our Story"
                  className="img-fluid rounded shadow-lg"
                />
              </div>
              <div className="col-lg-6">
                <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                  <h6 className="text-uppercase text-gold letter-spacing-2 mb-3">The Journey Begins</h6>
                  <h2 className="display-5 fw-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Our Story</h2>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    AR Perfume was born from a profound passion for the art of perfumery. Our founders traveled globally, seeking the rarest and most exquisite raw materials to create blends that transcend the ordinary.
                  </p>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>
                    What started as a small boutique has blossomed into a globally recognized luxury brand, celebrated for uncompromising quality and deeply evocative signature scents. We don't just make perfumes; we craft bottled memories.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* 3 & 4. Vision and Mission */}
        <section className="vision-mission py-5 bg-white">
          <div className="container py-4">
            <div className="row g-4 justify-content-center">
              <div className="col-md-6">
                <motion.div className="h-100 p-5 border rounded text-center position-relative overflow-hidden luxury-hover-card"
                  whileHover={{ y: -10 }} transition={{ duration: 0.3 }}>
                  <div className="icon-wrapper mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </div>
                  <h3 className="fw-bold mb-3" style={{ fontFamily: "inherit" }}>Our Vision</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>To be the global benchmark of luxury perfumery, transforming the way the world experiences scent by continuously redefining elegance and emotional connection through fragrance.</p>
                </motion.div>
              </div>
              <div className="col-md-6">
                <motion.div className="h-100 p-5 border rounded text-center position-relative overflow-hidden luxury-hover-card"
                  whileHover={{ y: -10 }} transition={{ duration: 0.3 }}>
                  <div className="icon-wrapper mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <circle cx="12" cy="12" r="6"></circle>
                      <circle cx="12" cy="12" r="2"></circle>
                    </svg>
                  </div>
                  <h3 className="fw-bold mb-3" style={{ fontFamily: "inherit" }}>Our Mission</h3>
                  <p className="text-muted" style={{ lineHeight: '1.8' }}>To meticulously craft exceptional, long-lasting fragrances using ethically sourced premium ingredients, delivering an unparalleled customer experience that inspires confidence in every spray.</p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Our Core Values */}
        <section className="core-values py-5 my-4">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Our Core Values</h2>
              <div className="divider mx-auto my-3" style={{ width: '50px', height: '2px', backgroundColor: '#d4af37' }}></div>
            </div>
            <div className="row g-4">
              {['Quality Excellence', 'Authenticity', 'Customer Satisfaction', 'Innovation', 'Trust & Transparency'].map((val, idx) => (
                <div className="col-lg-4 col-md-6" key={idx}>
                  <motion.div className="value-card p-4 bg-white rounded shadow-sm text-center h-100"
                    whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
                    <div className="value-icon mb-3">
                      <i className="fas fa-star text-gold fs-3"></i>
                    </div>
                    <h5 className="fw-bold">{val}</h5>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Why Choose AR Perfume */}
        <section className="why-choose-us py-5 bg-dark text-white">
          <div className="container py-5">
            <div className="text-center mb-5">
              <h6 className="text-gold letter-spacing-2 text-uppercase">The AR Difference</h6>
              <h2 className="display-5 fw-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Why Choose AR Perfume</h2>
            </div>
            <div className="row g-4">
              {[
                { title: 'Premium Ingredients', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" /></svg>, desc: "We source the finest raw materials globally, ensuring unparalleled depth and complexity." },
                { title: 'Long-Lasting', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>, desc: "Expertly concentrated formulas designed to stay with you from dawn until dusk." },
                { title: 'Unique Signatures', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /><path d="M17 12a5 5 0 0 0-5-5" /><path d="M12 17a5 5 0 0 1-5-5" /></svg>, desc: "Distinctive scent profiles intricately blended by master perfumers for true exclusivity." },
                { title: 'Affordable Luxury', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>, desc: "Experience prestige positioning and opulent quality without the luxury surcharge." },
                { title: 'Customer-Centric', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>, desc: "A personalized experience tailored to help you discover the perfect scent effortlessly." },
                { title: 'Trusted Brand', icon: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>, desc: "A celebrated reputation backed by thousands of globally satisfied perfume enthusiasts." }
              ].map((feature, idx) => (
                <div className="col-lg-4 col-md-6 text-center" key={idx}>
                  <div className="feature-card p-5 h-100 rounded d-flex flex-column align-items-center justify-content-center" style={{ backgroundColor: "rgba(255, 255, 255, 0.03)" }}>
                    <div className="feature-icon-wrapper mb-4" style={{ height: "70px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {feature.icon}
                    </div>
                    <h4 style={{ fontFamily: "inherit" }} className="fw-bold mb-3">{feature.title}</h4>
                    <p className="text-white-50 small mb-0" style={{ lineHeight: '1.6' }}>{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Collection Highlights */}
        <section className="collections py-5 my-5">
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Our Fragrance Collection</h2>
              <div className="divider mx-auto my-3" style={{ width: '50px', height: '2px', backgroundColor: '#d4af37' }}></div>
            </div>
            <div className="row g-4">
              {[
                { title: 'Luxury Perfumes', img: require('../assets/images/for-him/image-1.png') },
                { title: 'Daily Wear', img: require('../assets/images/for-him/image-2.png') },
                { title: 'Special Occasion', img: require('../assets/images/for-him/image-3.png') },
                { title: 'Gift Sets', img: require('../assets/images/for-him/image-4.png') }
              ].map((item, idx) => (
                <div className="col-lg-3 col-md-6" key={idx}>
                  <div className="collection-card position-relative rounded overflow-hidden shadow-sm">
                    <img src={item.img} alt={item.title} className="w-100 img-fluid" style={{ height: '300px', objectFit: 'cover' }} />
                    <div className="collection-overlay position-absolute bottom-0 w-100 p-3 bg-dark bg-opacity-75 text-white text-center">
                      <h5 className="mb-0">{item.title}</h5>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 8. Our Process */}
        <section className="our-process py-5 bg-light">
          <div className="container py-4">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Our Process</h2>
              <p className="text-muted">The meticulous journey from concept to your collection.</p>
            </div>
            <div className="row text-center g-4">
              {[
                { step: 1, title: 'Fragrance Selection', desc: 'Curating exquisite raw ingredients.' },
                { step: 2, title: 'Quality Testing', desc: 'Rigorous distillation and refinement.' },
                { step: 3, title: 'Packaging Excellence', desc: 'Housing scents in pure luxury.' },
                { step: 4, title: 'Customer Delivery', desc: 'Swift, safe transport to your door.' }
              ].map((process, idx) => (
                <div className="col-lg-3 col-md-6" key={idx}>
                  <div className="process-circle mx-auto bg-white rounded-circle shadow d-flex align-items-center justify-content-center mb-3 text-gold fs-2 fw-bold" style={{ width: '80px', height: '80px' }}>
                    {process.step}
                  </div>
                  <h5 className="fw-bold">{process.title}</h5>
                  <p className="text-muted small">{process.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Statistics */}
        <section className="stats-section py-5 px-3" style={{ backgroundImage: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url("https://images.unsplash.com/photo-1588405748880-12d1d2a59f75?auto=format&fit=crop&q=80&w=2000")', backgroundAttachment: 'fixed', backgroundSize: 'cover' }}>
          <div className="container py-5">
            <div className="row text-center text-white g-4">
              <div className="col-md-3 col-6">
                <h2 className="display-4 fw-bold text-gold">50k+</h2>
                <p className="text-uppercase letter-spacing-2 small">Happy Customers</p>
              </div>
              <div className="col-md-3 col-6">
                <h2 className="display-4 fw-bold text-gold">100k+</h2>
                <p className="text-uppercase letter-spacing-2 small">Products Sold</p>
              </div>
              <div className="col-md-3 col-6">
                <h2 className="display-4 fw-bold text-gold">99%</h2>
                <p className="text-uppercase letter-spacing-2 small">Satisfaction Rate</p>
              </div>
              <div className="col-md-3 col-6">
                <h2 className="display-4 fw-bold text-gold">15+</h2>
                <p className="text-uppercase letter-spacing-2 small">Years of Excellence</p>
              </div>
            </div>
          </div>
        </section>

        {/* 10. Testimonials Carousel */}
        <section className="testimonials py-5 my-4 bg-white">
          <div className="container py-4">
            <div className="text-center mb-5">
              <h2 className="display-5 fw-bold" style={{ fontFamily: "'Playfair Display', serif" }}>What Our Customers Say</h2>
              <div className="divider mx-auto my-3" style={{ width: '50px', height: '2px', backgroundColor: '#d4af37' }}></div>
            </div>
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              autoplay={{ delay: 3000 }}
              pagination={{ clickable: true }}
              className="pb-5"
            >
              {testimonials.map((test, idx) => (
                <SwiperSlide key={idx}>
                  <div className="testimonial-card p-4 rounded bg-light text-center h-100 shadow-sm">
                    <div className="text-gold mb-3">
                      {[...Array(test.rating)].map((_, i) => <i className="fas fa-star" key={i}></i>)}
                    </div>
                    <p className="fst-italic text-muted mb-4">"{test.text}"</p>
                    <h6 className="fw-bold m-0">- {test.name}</h6>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        {/* 12. Call to Action */}
        <section className="cta-section py-5 text-center bg-white">
          <div className="container py-5">
            <h2 className="display-5 fw-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Discover the Essence of Luxury with AR Perfume</h2>
            <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '600px' }}>Indulge in our exquisite collections and find the signature scent that tells your story perfectly.</p>
            <div className="d-flex justify-content-center gap-3 mt-4 flex-wrap">
              <Link to="/products" className="btn btn-dark px-4 py-2" style={{ borderRadius: '0', letterSpacing: '1px' }}>SHOP NOW</Link>
              <Link to="/products" className="btn btn-outline-dark px-4 py-2" style={{ borderRadius: '0', letterSpacing: '1px' }}>EXPLORE COLLECTION</Link>
              <Link to="/contact" className="btn text-white px-4 py-2" style={{ backgroundColor: '#d4af37', border: 'none', borderRadius: '0', letterSpacing: '1px' }}>CONTACT US</Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default About;
